import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff, Loader2, CheckCircle, ArrowLeft } from 'lucide-react';
import { extractErrorMessage } from '../components/SafeError';
import AuthHeader from '../components/AuthHeader';

const RegisterPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const { register } = useAuth();
  const navigate = useNavigate();

  // Updated password requirements - simpler for consumer SaaS
  const passwordRequirements = [
    { met: password.length >= 8, text: 'At least 8 characters' },
    { met: /[a-zA-Z]/.test(password), text: 'At least one letter' },
    { met: /\d/.test(password), text: 'At least one number' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (!/[a-zA-Z]/.test(password)) {
      setError('Password must contain at least one letter');
      return;
    }

    if (!/\d/.test(password)) {
      setError('Password must contain at least one number');
      return;
    }

    setLoading(true);

    try {
      await register(email, password);
      setSuccess(true);
    } catch (err) {
      // err.message is already extracted properly by AuthContext
      setError(typeof err.message === 'string' ? err.message : extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen auth-bg flex items-center justify-center p-4" style={{ backgroundImage: 'url(/cockpit/hero.jpg)' }}>
        <div className="absolute inset-0 bg-black/80" />
        
        <div className="relative w-full max-w-md" data-testid="register-success">
          <div className="card-panel p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-status-success/20 rounded-full mb-4">
              <CheckCircle className="w-8 h-8 text-status-success" />
            </div>
            <h1 className="text-2xl font-semibold text-cockpit-text-primary mb-2">Check Your Email</h1>
            <p className="text-cockpit-text-secondary mb-6">
              We've sent a verification link to <strong className="text-cockpit-text-primary">{email}</strong>. 
              Please check your inbox and click the link to verify your account.
            </p>
            <Link to="/login" className="btn-primary inline-block">
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen auth-bg flex items-center justify-center p-4" style={{ backgroundImage: 'url(/cockpit/hero.jpg)' }}>
      <div className="absolute inset-0 bg-black/80" />
      
      <div className="relative w-full max-w-md" data-testid="register-page">
        <div className="card-panel p-8">
          {/* Back to Login link - Critical UX fix */}
          <Link 
            to="/login" 
            className="flex items-center gap-1.5 text-sm text-cockpit-text-secondary hover:text-airbus-blue transition-colors mb-6"
            data-testid="back-to-login"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Login
          </Link>

          {/* Header */}
          <AuthHeader title="Create Account" subtitle="Join Flight Sim Academy" />

          {/* Error message */}
          {error && (
            <div className="mb-6 p-3 bg-red-900/30 border border-red-900 rounded-sm">
              <p className="text-sm text-red-300" data-testid="register-error">{error}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="label-text block mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-field"
                placeholder="pilot@example.com"
                required
                data-testid="register-email"
              />
            </div>

            <div>
              <label className="label-text block mb-2">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-field pr-10"
                  placeholder="••••••••"
                  required
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-cockpit-text-secondary hover:text-cockpit-text-primary"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {/* Password requirements - simplified */}
              <div className="mt-2 space-y-1">
                {passwordRequirements.map((req, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <div className={`w-1.5 h-1.5 rounded-full ${req.met ? 'bg-status-success' : 'bg-cockpit-border'}`} />
                    <span className={req.met ? 'text-status-success' : 'text-cockpit-text-secondary'}>{req.text}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <label className="label-text block mb-2">Confirm Password</label>
              <input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="input-field"
                placeholder="••••••••"
                required
                data-testid="register-confirm-password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary flex items-center justify-center gap-2"
              data-testid="register-submit"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {loading ? 'Creating account...' : 'Create Account'}
            </button>
          </form>

          {/* Footer */}
          <div className="mt-6 text-center">
            <p className="text-sm text-cockpit-text-secondary">
              Already have an account?{' '}
              <Link to="/login" className="text-airbus-blue hover:underline" data-testid="login-link">
                Sign in
              </Link>
            </p>
            <a
              href="https://flightsimacademy.co.uk"
              className="block mt-3 text-xs text-cockpit-text-secondary opacity-70 hover:opacity-100 transition-opacity"
              data-testid="back-to-website-link"
            >
              ← flightsimacademy.co.uk
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
